// Cross-corpus word-gloss consensus — the pure core (functional-programming
// policy) behind `convex/ai.ts`'s derived-row fan-out and `wordTranslation`
// query. Ported from lekterion's `convex/cache.ts` (`parseLiteral`,
// `seedDerivedRows`' dedupe, `normTranslation`, the weighted arg-max vote).
//
// The idea: a multi-word gloss result's `literal` field ("den = the · Weg =
// way") already contains one small translation per source word. Fanning those
// out into per-word cache rows (marked `derived`) is what makes single words
// individually cached at all — and the consensus query then answers "what does
// this word mean anywhere else in the corpus" as a weighted vote over every
// cached row of the same casefolded spelling.
//
// Pure module: no Convex imports beyond nothing at all — both the client (the
// engagement gate's cached-word probe) and the server depend on it.

/** One `src = gloss` unit split out of a `literal` string. */
export interface LiteralUnit {
  src: string;
  gloss: string;
}

/**
 * The separators a model plausibly puts between interlinear units. The prompt
 * asks for U+00B7 MIDDLE DOT, but U+0387 GREEK ANO TELEIA is visually
 * identical and the natural character for a model working in Greek; a bullet,
 * a pipe, a newline, or a semicolon are the other realistic substitutions.
 * Missing one is not a loud failure — the whole literal collapses into a
 * single "unit" whose gloss is the rest of the interlinear — so `isWordSized`
 * backstops this set rather than trusting it to be complete.
 */
const UNIT_SEPARATORS = /[\u00b7\u0387\u2022|;\n\r]/u;

/**
 * Split a multi-word `literal` ("den = the · Weg = way · …") into its
 * per-word units. This parses LLM output, so it must survive malformed input:
 * a chunk without `=` yields an empty `src` (dropped downstream), an empty
 * gloss drops the unit, stray separators yield empty chunks that vanish. The
 * FIRST `=` splits — a gloss may itself contain `=`.
 */
export function parseLiteral(literal: string): LiteralUnit[] {
  return literal
    .split(UNIT_SEPARATORS)
    .map((chunk) => {
      const eq = chunk.indexOf("=");
      return eq === -1
        ? { src: "", gloss: chunk.trim() }
        : { src: chunk.slice(0, eq).trim(), gloss: chunk.slice(eq + 1).trim() };
    })
    .filter((u) => u.gloss !== "");
}

/** Typographic quotes and their ASCII forms — folded together for the key. */
const CURLY_QUOTES = /[‘’“”]/gu;

/**
 * Flatten typographic quotes to their ASCII forms. EVERY `aiCache` key goes
 * through this — `quoteLower` here, `selectionHash` in `selection.ts` — so a
 * selection is one cache row whether the text it came from was educated
 * (`curlQuotes`) or authored straight. Nothing else about the text changes;
 * the displayed `quote` column keeps the reader's own marks.
 */
export function foldTypographicQuotes(text: string): string {
  return text.replace(CURLY_QUOTES, (q) =>
    q === "‘" || q === "’" ? "'" : '"',
  );
}

/**
 * Casefold a source word to its canonical index form: NFC (polytonic Greek
 * has composed/decomposed spellings of the same letters) + lowercase + ASCII
 * quotes. The ONE function both the `quoteLower` column write and every
 * consensus lookup go through, so the two can never disagree.
 *
 * The quote fold is what keeps this cache addressable across the reader's
 * typographic quotes (`curlQuotes` educates the displayed text, so a selection
 * arrives as `don’t`) and the straight quotes the corpus is authored in — and
 * across books whose source is already curly. Two spellings of one word must
 * not be two cache rows, each paying for its own Anthropic call.
 */
export function foldQuote(quote: string): string {
  return foldTypographicQuotes(quote.normalize("NFC").toLowerCase());
}

/** A derived per-word row to seed from one gloss result's `literal`. */
export interface DerivedWord {
  word: string;
  wordLower: string;
  translation: string;
}

/** Longest a single word's literal gloss can plausibly be. */
const MAX_GLOSS_LENGTH = 40;

/**
 * Hard cap on the fan-out of one `literal`. The realistic worst case is well
 * under this; the point is that the number of rows a single gloss writes is
 * bounded by THIS constant and not by how much the model chose to emit.
 */
const MAX_DERIVED_WORDS = 100;

/**
 * Is this unit word-sized — safe to write into the public cache as one word's
 * translation? A derived row is permanent, votes forever, and wins outright
 * when it is the only row for its word, so a malformed unit must be dropped
 * rather than stored. The three ways a unit is not word-sized:
 *
 * - the gloss still contains `=`, so it swallowed later units — the signature
 *   of a separator `UNIT_SEPARATORS` does not know about;
 * - the gloss is far longer than any word's gloss, likewise;
 * - the source is several words, so it is a phrase, not a word.
 */
function isWordSized({ src, gloss }: LiteralUnit): boolean {
  return (
    !/\s/u.test(src) && !gloss.includes("=") && gloss.length <= MAX_GLOSS_LENGTH
  );
}

/**
 * The distinct, non-empty, word-sized source words of a `literal`, each with
 * its translation — the fan-out `insertGlossRow` seeds. Deduped by casefolded
 * spelling (first occurrence wins, matching lekterion), so "den Weg den" seeds
 * `den` once, and capped at `MAX_DERIVED_WORDS`.
 */
export function derivedWords(literal: string): DerivedWord[] {
  const seen = new Set<string>();
  const out: DerivedWord[] = [];
  for (const unit of parseLiteral(literal)) {
    if (out.length === MAX_DERIVED_WORDS) break;
    if (!isWordSized(unit)) continue;
    const wordLower = foldQuote(unit.src);
    if (wordLower === "" || seen.has(wordLower)) continue;
    seen.add(wordLower);
    out.push({ word: unit.src, wordLower, translation: unit.gloss });
  }
  return out;
}

/**
 * The namespaced cache key of a derived per-word row: `#` never occurs in a
 * hex SHA-256, so these rows are structurally invisible to `getCached` (which
 * only ever receives pure-hex hashes) while living in the same table + index.
 */
export function derivedRowKey(parentHash: string, wordLower: string): string {
  return `${parentHash}#${wordLower}`;
}

/**
 * Punctuation stripped from BOTH ends of a translation. One class, applied
 * symmetrically: an asymmetric pair (brackets closing but not opening, say)
 * puts "(the)" in a different bucket from "the" and quietly splits the vote,
 * which is the opposite of what this function is for.
 */
const EDGE_PUNCTUATION_CLASS = "[\\s.,;:!?'\"»«›‹—–\\-()\\[\\]{}…]";
const EDGE_PUNCTUATION = new RegExp(
  `^${EDGE_PUNCTUATION_CLASS}+|${EDGE_PUNCTUATION_CLASS}+$`,
  "gu",
);

/**
 * Fold a translation to its consensus bucket key: NFC, casefold, strip edge
 * punctuation. NFC comes first for the same reason `foldQuote` does it on the
 * key side — a decomposed "café" and a composed one are the same translation,
 * and folding only one side of the cache asymmetrically would be worse than
 * folding neither.
 *
 * A trailing parenthetical stays in the key, so "bear (animal)" and "bear (to
 * carry)" keep their own buckets. It reads like noise next to "the (masc.)",
 * but a parenthetical is at least as often the SENSE DISCRIMINATOR, and this
 * cache's corpus is Homeric Greek and German — homographs are the normal case,
 * not the edge one. Pooling senses would pick one arbitrarily AND inflate the
 * "agreed across N cached passages" count the reader is shown, so the card
 * would assert confidence over rows that meant different things. A fragmented
 * bucket is cosmetic; a merged one is wrong.
 *
 * The edge strip does eat the closing bracket, so "the (masc.)" keys as
 * "the (masc" — lopsided, but harmless and in fact useful: this value is only
 * ever a bucket key (`consensusVote` displays the row's original
 * `translation`), and it pools "(masc.)" with "(masc)".
 */
export function normalizeTranslation(s: string): string {
  return s.normalize("NFC").toLowerCase().replace(EDGE_PUNCTUATION, "");
}

/** The slice of a cache row the consensus vote reads. */
export interface ConsensusRow {
  translation: string;
  derived: boolean;
}

export interface Consensus {
  /** Winning translation, in its first-seen surface form. */
  translation: string;
  /** Rows that voted for the winner. */
  count: number;
  /** All rows that cast a vote (non-empty translation). */
  total: number;
}

// A real single-word translation (someone glossed the word on its own, in
// context) outweighs a `derived` per-word split from a phrase's literal — the
// contextual reading wins when both exist; derived votes still decide when no
// real row is cached, and accumulate across passages.
const REAL_WEIGHT = 3;
const DERIVED_WEIGHT = 1;

/**
 * Weighted arg-max over every cached translation of one word. Rows whose
 * translation normalizes to nothing cast no vote; ties keep the first-seen
 * bucket (deterministic in row order — index order, so stable).
 */
export function consensusVote(rows: readonly ConsensusRow[]): Consensus | null {
  const buckets = new Map<
    string,
    { translation: string; weight: number; count: number }
  >();
  let total = 0;
  for (const row of rows) {
    const norm = normalizeTranslation(row.translation);
    if (norm === "") continue;
    total++;
    const weight = row.derived ? DERIVED_WEIGHT : REAL_WEIGHT;
    const bucket = buckets.get(norm);
    if (bucket) {
      bucket.weight += weight;
      bucket.count++;
    } else {
      buckets.set(norm, { translation: row.translation, weight, count: 1 });
    }
  }
  let best: { translation: string; weight: number; count: number } | null =
    null;
  for (const bucket of buckets.values()) {
    if (best === null || bucket.weight > best.weight) best = bucket;
  }
  return best === null
    ? null
    : { translation: best.translation, count: best.count, total };
}
